import React, { useContext, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import { getImageURL } from 'utils/cloudinary';
import { allowMarketplace } from 'utils/employee';
import {
  excludeTerminatedAlegeusAccountsForTerminatedEmployees,
  excludeTerminatedFormaAccountsForEmployees,
  filterActiveAlegeusPretaxAccountsWithFsaType,
  filterActiveFormaPretaxAccounts,
} from 'utils/pretaxAccount';
import {
  RecognitionRoutes,
  StoreRoutes,
  ClaimRoutes,
  AccountRoutes,
  WelcomeRoutes,
} from 'utils/routeGenerator';

import TopNavUser from 'components/common/TopNavUser';
import styled from 'styled-components';
import NavigationBar from '@joinforma/ui/components/NavigationBar';
import NavigationItem from '@joinforma/ui/components/NavigationItem';
import { ReactComponent as WalletSvg } from '@joinforma/ui/assets/icons/wallet.svg';
import { ReactComponent as HomeSvg } from '@joinforma/ui/assets/icons/home.svg';
import { ReactComponent as StoreSvg } from '@joinforma/ui/assets/icons/store.svg';
import { ReactComponent as StartSvg } from '@joinforma/ui/assets/icons/star.svg';
import { ReactComponent as DocumentSvg } from '@joinforma/ui/assets/icons/document.svg';
import { SCREEN_WIDTHS } from '@joinforma/ui/constants/breakpoints';

import { Employee, Company } from './types';
import PretaxContext from 'contexts/PretaxContext';
import { FEATURE_FLAGS } from 'constants/featureFlag';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { PretaxAccount, PretaxWallet } from 'contexts/PretaxContext/types';
import useQueryProfile from 'hooks/useQueryProfile';
import {
  PRETAX_ACCOUNT_TYPES,
  PRETAX_ACCOUNT_V2_TYPES,
} from 'constants/accounts';

const TopNavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.08);
  height: 67px;
  padding: 0 5px;
  background-color: ${({
    theme: {
      colors: {
        background: { primary },
      },
    },
  }) => primary};
  position: sticky;
  top: 0;
  // this needs to be higher to be on top of search input
  z-index: 2;

  @media (min-width: ${SCREEN_WIDTHS.MOBILE_LANDSCAPE}) {
    padding: 0 32px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
`;

const DesktopNavigationBar = styled(NavigationBar)`
  display: none;

  @media (min-width: ${SCREEN_WIDTHS.LAPTOP}) {
    display: flex;
  }
`;

const LogoSection = styled.div`
  margin-right: 40px;
  height: 100%;
`;

const HomeLink = styled(NavLink)`
  height: 100%;
  display: flex;
  align-items: center;
`;

const Logo = styled.img.attrs({
  alt: 'Company Logo',
  role: 'link',
  'aria-label': 'Redirect back to landing page',
})`
  max-width: 130px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

interface Props {
  employee: Employee;
  company: Company;
  new_home_page_enabled: boolean;
  current_path: string;
}

const TopNavigationBar = ({
  employee,
  company,
  new_home_page_enabled,
  current_path,
}: Props) => {
  const {
    alegeus_pretax_accounts,
    pretax_accounts: forma_pretax_accounts,
    new_pretax_experience,
    updateWallets,
  } = useContext(PretaxContext);

  const { data: profile_data, is_loading: is_profile_v3_loading } =
    useQueryProfile({
      isEnabled: Boolean(employee.id && new_pretax_experience),
      use_v3_profile: new_pretax_experience,
    });

  const feature_flag = useContext(FeatureFlagContext);

  const is_alegeus_migrated_customer: boolean = feature_flag.flag(
    FEATURE_FLAGS.ALEGEUS_MIGRATED_CUSTOMER,
    false
  );

  const is_forma_pretax_exp = useMemo(
    () => is_alegeus_migrated_customer && new_pretax_experience,
    [is_alegeus_migrated_customer, new_pretax_experience]
  );

  const pretax_accounts = useMemo(
    () =>
      is_forma_pretax_exp ? forma_pretax_accounts : alegeus_pretax_accounts,
    [is_forma_pretax_exp, alegeus_pretax_accounts, forma_pretax_accounts]
  );

  const { is_terminated } = employee;

  const active_pretax_accounts = is_forma_pretax_exp
    ? excludeTerminatedFormaAccountsForEmployees({
        pretax_accounts: pretax_accounts as PretaxWallet[],
      })
    : excludeTerminatedAlegeusAccountsForTerminatedEmployees({
        is_terminated,
        pretax_accounts: pretax_accounts as PretaxAccount[],
      });

  // Only fsa, hsa pretax accounts can have store enabled
  const store_eligible_pretax_accounts = is_forma_pretax_exp
    ? filterActiveFormaPretaxAccounts({
        pretax_accounts: active_pretax_accounts as PretaxWallet[],
      })
    : filterActiveAlegeusPretaxAccountsWithFsaType({
        pretax_accounts: active_pretax_accounts as PretaxAccount[],
      });

  const is_marketplace_enabled = allowMarketplace(employee, company);
  const has_active_pretax_accounts =
    store_eligible_pretax_accounts &&
    store_eligible_pretax_accounts?.length > 0;
  const is_claims_enabled = company.features.reimbursement;
  const is_recognitions_enabled = company.features.recognition;

  const show_store = is_marketplace_enabled || has_active_pretax_accounts;

  const show_claims = useMemo(() => {
    if (is_claims_enabled) {
      // For forma pretax accounts, if user only has HSA type accounts, don't show claims
      if (is_forma_pretax_exp) {
        return (
          pretax_accounts.length === 0 ||
          !(pretax_accounts as PretaxWallet[]).every(
            (wallet) =>
              Object.hasOwn(wallet, 'wallet_type_id') &&
              wallet.wallet_type_id === PRETAX_ACCOUNT_V2_TYPES.HSA
          )
        );
      }

      return (
        // Show claims nav link even if there are no accounts
        pretax_accounts.length === 0 ||
        // For alegeus pretax accounts, if user only has HSA type accounts, don't show claims
        !(pretax_accounts as PretaxAccount[]).every(
          (account) =>
            Object.hasOwn(account, 'account_type') &&
            (account.account_type === PRETAX_ACCOUNT_TYPES.HSA ||
              account.account_type === PRETAX_ACCOUNT_TYPES.WCS ||
              account.account_type === PRETAX_ACCOUNT_TYPES.LPFSAHSA)
        )
      );
    }

    // Show claims even if disabled at company features level if user has pretax accounts
    return is_forma_pretax_exp
      ? pretax_accounts.length > 0 &&
          !(pretax_accounts as PretaxWallet[]).every(
            (wallet) =>
              Object.hasOwn(wallet, 'wallet_type_id') &&
              wallet.wallet_type_id === PRETAX_ACCOUNT_V2_TYPES.HSA
          )
      : pretax_accounts.length > 0 &&
          !(pretax_accounts as PretaxAccount[]).every(
            (account) =>
              Object.hasOwn(account, 'account_type') &&
              (account.account_type === PRETAX_ACCOUNT_TYPES.HSA ||
                account.account_type === PRETAX_ACCOUNT_TYPES.WCS ||
                account.account_type === PRETAX_ACCOUNT_TYPES.LPFSAHSA)
          );
  }, [is_claims_enabled, is_forma_pretax_exp, pretax_accounts]);

  const show_recognitions = is_recognitions_enabled;

  const showNavBar = ({ employee, company }: $TSFixMe) => {
    if (company.settings.skip_onboarding) {
      return true;
    }
    return employee.is_profile_complete;
  };

  useEffect(() => {
    // TODO: refactor this fetch into current user context after we
    // fully enabled the new pretax experience to avoid this extra query
    if (new_pretax_experience && !is_profile_v3_loading && profile_data) {
      const {
        employee: { employee_wallets: employee_wallets_v3 },
      } = profile_data;
      // setEmployeeWallets(employee_wallets_v3);
      updateWallets(employee_wallets_v3);
    }
  }, [
    profile_data,
    new_pretax_experience,
    is_profile_v3_loading,
    updateWallets,
  ]);

  return (
    <TopNavContainer>
      <LeftSection>
        <LogoSection>
          <HomeLink to="/">
            <Logo
              src={getImageURL(company.company_info.logo_url, {
                width: 130,
              })}
            />
          </HomeLink>
        </LogoSection>
        {employee && showNavBar({ employee, company }) && (
          <DesktopNavigationBar>
            {new_home_page_enabled && (
              <NavigationItem
                label="Home"
                icon={HomeSvg}
                to={WelcomeRoutes.homeFeed()}
                active={
                  current_path.startsWith('/home-feed') || current_path === '/'
                }
              />
            )}
            {show_store && (
              <NavigationItem
                label="Store"
                icon={StoreSvg}
                to={
                  allowMarketplace(employee, company)
                    ? StoreRoutes.listExplore()
                    : StoreRoutes.detailPretax({
                        pretax_account: 'home',
                      })
                }
                active={current_path.startsWith('/explore')}
              />
            )}
            <NavigationItem
              label={'Benefits'}
              icon={WalletSvg}
              to={AccountRoutes.list()}
              active={current_path.startsWith('/accounts')}
            />
            {show_claims && (
              <NavigationItem
                label="Claims"
                icon={DocumentSvg}
                to={ClaimRoutes.list()}
                active={current_path.startsWith('/claims')}
              />
            )}
            {show_recognitions && (
              <NavigationItem
                label="Recognition"
                icon={StartSvg}
                to={RecognitionRoutes.list()}
                active={current_path.startsWith('/recognition')}
              />
            )}
          </DesktopNavigationBar>
        )}
      </LeftSection>

      <RightSection>
        <TopNavUser
          employee={employee}
          company={company}
          pathname={current_path}
          show_store={show_store}
          show_claims={show_claims}
          show_recognitions={show_recognitions}
        />
      </RightSection>
    </TopNavContainer>
  );
};

export default TopNavigationBar;
